import { NamePath } from 'antd/lib/form/interface';
import { Rule } from 'rc-field-form/lib/interface';
import { FieldPath, FieldPathValue } from 'react-hook-form';
import { getI18n } from 'react-i18next';
import { isValidPhoneNumber } from 'react-phone-number-input/max';
import { ITicketFormValidation } from '../../../types/form';
import { customFieldTypeMap } from '../../components/CustomField/CustomField.type';
import { UIFieldRuleOnForm } from '../../hooks/ticketDetailPage/useAgentFormById';
import {
    FieldRequirementTypeMap,
    IFieldDefinition,
    TICKET_FORM_FIELDS_KEY,
    TicketSystemFieldsKey,
    TicketSystemFieldsKeyMap
} from '../../model/types';
import { nonNullishValue, nullishValue } from '../helpers';
import {
    parseEmailFromSerializedValue,
    parseFullNameFromSerializedUntrustedEndUserValue,
    parsePhoneNumberFromSerializedValue
} from './formHelper.parser';
import { FormSystemFields } from './formHelpers';

export const phoneValidator = async (_rule: unknown, value: string, errorMessage: string): Promise<boolean> => {
    try {
        if (typeof value === 'undefined' || !value || value.length === 0) return true;

        const isPhoneNumber = isValidPhoneNumber(value);
        if (isPhoneNumber) return true;

        throw new Error();
    } catch (error) {
        throw new Error(errorMessage);
    }
};

export const isEmail = (value: string): boolean => {
    const regExp =
        // eslint-disable-next-line
        /^(([^<>()\[\]\\.,;:\s@"\u0080-\uFFFF]+(\.[^<>()\[\]\\.,;:\s@"\u0080-\uFFFF]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i; // see details https://www.debuggex.com/r/m3otJ4X2PJ8Gml6_

    return (parseEmailFromSerializedValue(value) || value)?.match(regExp)?.length > 0;
};
export const emailValidator = async (_rule: unknown, value: string[], errorMessage: string): Promise<boolean> => {
    try {
        // validation takes part here
        let result = true;
        if (typeof value === 'undefined' || value.length === 0) return true;

        if (value.length === 0) return result;
        if (typeof value === 'string') {
            if (isEmail(value)) return true;
            throw new Error(errorMessage);
        }

        value.forEach((v: string) => {
            result = result && isEmail(v.split(':')[1] ?? v);
        });

        if (!result) throw new Error(errorMessage);

        return true;
    } catch (error) {
        throw new Error(errorMessage);
    }
};

export const emailPhoneValidator = async (_rule: unknown, value: string, errorMessage: string): Promise<boolean> => {
    try {
        if (typeof value === 'undefined' || value.length === 0) return true;
        // validate email or phone-number here
        const isValidEmail = isEmail(value.split(':')[1] ?? value);
        if (isValidEmail) return true;

        const isPhoneNumber = isValidPhoneNumber(value.split(':')[2] ?? value);
        if (isPhoneNumber) return true;

        throw new Error();
    } catch (error) {
        throw new Error(errorMessage);
    }
};

export const requiredToSolveValidator = (dependencies: NamePath[], errorMessage: string): Rule =>
    dependencies
        ? ({ getFieldValue }) => ({
              validator(_, value) {
                  if (!value && getFieldValue([FormSystemFields.name, TicketSystemFieldsKey.status]) === 'Solved') {
                      return Promise.reject(new Error(errorMessage));
                  }
                  return Promise.resolve();
              }
          })
        : null;

// const isEmailOrPhoneNumber = (value: string): boolean =>
//     isEmail(parseEmailFromSerializedValue(value) || value) ||
//     isValidPhoneNumber(parsePhoneNumberFromSerializedValue(value) || value);

const isValidUser = (fieldKey: string) => (value: any) => {
    if (nullishValue(value)) return true;
    //HACK: Bünyamin => we have a modal for create requester now, we dont need validation for requester anymore
    if (fieldKey === TicketSystemFieldsKey.requester) return true;
    //TODO: Bünyamin => when we activate 'allow new value' we should validate.
    // if (value.split(',').length > 1)
    //     return (
    //         value.split(',').every((value) => isEmailOrPhoneNumber(parseSerializedValueFromUserFormValue(value))) ||
    //         (getI18n().t('form.email.invalid', { ns: 'forms' }) as string)
    //     );
    // return (
    //     isEmailOrPhoneNumber(parseSerializedValueFromUserFormValue(value)) ||
    //     (getI18n().t('form.emailPhone.invalid', { ns: 'forms' }) as string)
    // );
    //TODO: ts.email_ccs field validation rule is allow user and valid email
    if (fieldKey === TicketSystemFieldsKey.emailCc) {
        return (
            value
                .toString()
                .split(',')
                .every((value) => {
                    if (value.includes('|')) {
                        if (!Number.isNaN(+value.split('|')[0])) return true;
                    } else {
                        return isEmail(value);
                    }
                }) || (getI18n().t('form.email.invalid', { ns: 'forms' }) as string)
        );
    }
};

type RequiredStatusType = 1 | 2 | 3 | 4 | 5 | 6;
function getI18nKeyForRequiredForStatusValidation(status: RequiredStatusType) {
    switch (status) {
        case 1:
            return 'form.requiredForNew';
        case 2:
            return 'form.requiredForOpen';
        case 3:
            return 'form.requiredForPending';
        case 4:
            return 'form.requiredForSolved';
        case 5:
            return 'form.requiredForClosed';
        case 6:
            return 'form.requiredForOnHold';
    }
}

function getRequiredStatusErrorTextByCurrentStatus(currentStatus: number, requiredStatusList: number[]) {
    const requiredStatus = requiredStatusList.find((s) => s === currentStatus) as RequiredStatusType;
    return getRequiredStatusErrorText(requiredStatus);
}

const getRequiredStatusErrorText = <T extends RequiredStatusType>(status: T) =>
    getI18n().t(getI18nKeyForRequiredForStatusValidation(status), { ns: 'forms' });

const getRequiredErrorText = () => getI18n().t('form.required', { ns: 'forms' }) as string;

const grispiCheckboxFieldRequiredValidation = <T>(value: FieldPathValue<T, FieldPath<T>>, _formValues: T) =>
    value === 'true' || getRequiredErrorText();

const grispiDefaultFieldRequiredValidation = <T>(value: FieldPathValue<T, FieldPath<T>>, _formValues: T) =>
    nonNullishValue(value) || getRequiredErrorText();

const defineGrispiFieldRequirementValidation = (fieldDefinition: IFieldDefinition) =>
    fieldDefinition.type === customFieldTypeMap.CHECKBOX
        ? grispiCheckboxFieldRequiredValidation
        : grispiDefaultFieldRequiredValidation;

const defineGrispiFieldRequirementForStatusValidation =
    (fieldDefinition: IFieldDefinition, requiredStatusList: number[]) =>
    <T>(value: FieldPathValue<T, FieldPath<T>>, formValues: T) => {
        const currentStatus = +formValues[TICKET_FORM_FIELDS_KEY][TicketSystemFieldsKeyMap.status];

        const noRequirementForCurrentStatus = !requiredStatusList.includes(currentStatus);

        return fieldDefinition.type === customFieldTypeMap.CHECKBOX
            ? noRequirementForCurrentStatus ||
                  value === 'true' ||
                  getRequiredStatusErrorTextByCurrentStatus(currentStatus, requiredStatusList)
            : noRequirementForCurrentStatus ||
                  nonNullishValue(value) ||
                  getRequiredStatusErrorTextByCurrentStatus(currentStatus, requiredStatusList);
    };

const defineGrispiFieldRequirementByConditionValidation =
    (fieldDefinition: IFieldDefinition, requiredConditionList: string[], conditionDeciderFieldKey: string) =>
    <T>(value: FieldPathValue<T, FieldPath<T>>, formValues: T) => {
        const noRequirementForCurrentConditionDeciderFieldValue = !requiredConditionList.includes(
            formValues[TICKET_FORM_FIELDS_KEY][
                `${conditionDeciderFieldKey.replace(new RegExp('\\.', 'gi'), '$')}`
            ] as string
        );

        return fieldDefinition.type === customFieldTypeMap.CHECKBOX
            ? noRequirementForCurrentConditionDeciderFieldValue ||
                  grispiCheckboxFieldRequiredValidation(value as any, formValues)
            : noRequirementForCurrentConditionDeciderFieldValue ||
                  grispiDefaultFieldRequiredValidation(value as any, formValues);
    };

const defineGrispiFieldNoRequirementByConditionValidation =
    (requiredConditionList: string[], conditionDeciderFieldKey: string, lastDefaultRule) =>
    <T>(value: FieldPathValue<T, FieldPath<T>>, formValues: T) => {
        const applyRequirementForCurrentConditionDeciderFieldValue = !requiredConditionList.includes(
            formValues[TICKET_FORM_FIELDS_KEY][
                `${conditionDeciderFieldKey.replace(new RegExp('\\.', 'gi'), '$')}`
            ] as string
        );

        return applyRequirementForCurrentConditionDeciderFieldValue ? lastDefaultRule : true;
    };
const fullNameValidation = (fullName: string) => {
    if (nullishValue(fullName)) return getI18n().t('form.required', { ns: 'forms' });
    const finishEmptySpacePattern = /^.*(\S)$/;
    if (!finishEmptySpacePattern.test(fullName))
        return getI18n().t('form.fullName.cannotFinishWithEmptySpace', { ns: 'forms' });
    const cannotStartWithEmptySpace = /^(\S).*$/;
    if (!cannotStartWithEmptySpace.test(fullName))
        return getI18n().t('form.fullName.cannotStartWithEmptySpace', { ns: 'forms' });
    const cannotStartWithDash = /^(?!-).*$/;
    if (!cannotStartWithDash.test(fullName)) return getI18n().t('form.fullName.cannotStartWithDash', { ns: 'forms' });
    const cannotHaveCharacters = /^(\p{L}|\s|-)*$/gu;
    if (!cannotHaveCharacters.test(fullName)) return getI18n().t('form.fullName.cannotHaveCharacters', { ns: 'forms' });
    return false;
};
type ValidationOptions = {
    fieldDefinition: IFieldDefinition;
    fieldRuleOnForm?: UIFieldRuleOnForm;
};

const isRequired = (requirementType: FieldRequirementTypeMap) => requirementType === FieldRequirementTypeMap.TRUE;
const isNotRequired = (requirementType: FieldRequirementTypeMap) => requirementType === FieldRequirementTypeMap.FALSE;

const isRequiredForStatus = (requirementType: FieldRequirementTypeMap) =>
    requirementType === FieldRequirementTypeMap.TARGET_STATUS;

export const getValidationRules = <T>({
    fieldRuleOnForm,
    fieldDefinition
}: ValidationOptions): ITicketFormValidation<T> => {
    let validate: ITicketFormValidation<T> = {};

    if (isRequired(fieldDefinition.required.type)) {
        validate.grispiRequiredRules = defineGrispiFieldRequirementValidation(fieldDefinition);
    }

    if (isRequiredForStatus(fieldDefinition.required.type)) {
        validate.grispiRequiredRules = defineGrispiFieldRequirementForStatusValidation(
            fieldDefinition,
            fieldDefinition.required.ids
        );
    }
    if (fieldRuleOnForm) {
        if (isRequired(fieldRuleOnForm.required.type)) {
            validate.grispiRequiredRules = defineGrispiFieldRequirementValidation(fieldDefinition);
        }
        if (isNotRequired(fieldRuleOnForm.required.type)) {
            validate = {} as ITicketFormValidation<T>;
        }
        if (isRequiredForStatus(fieldRuleOnForm.required.type)) {
            validate.grispiRequiredRules = defineGrispiFieldRequirementForStatusValidation(
                fieldDefinition,
                fieldRuleOnForm.required.ids
            );
        }
        // const lastApplicableCondition = lastApplicableFieldRuleOnForm?.conditions?.at(-1);
        const lastApplicableCondition = fieldRuleOnForm?.listenedConditionOnForm;

        if (lastApplicableCondition) {
            if (isRequired(lastApplicableCondition.action?.required?.type as FieldRequirementTypeMap)) {
                validate.grispiRequiredRules = defineGrispiFieldRequirementByConditionValidation(
                    fieldDefinition,
                    lastApplicableCondition.value,
                    lastApplicableCondition.conditionFieldKey
                );
            }
            if (isNotRequired(lastApplicableCondition.action?.required?.type as FieldRequirementTypeMap)) {
                validate.grispiRequiredRules = defineGrispiFieldNoRequirementByConditionValidation(
                    lastApplicableCondition.value,
                    lastApplicableCondition.conditionFieldKey,
                    validate.grispiRequiredRules
                );
            }

            if (isRequiredForStatus(lastApplicableCondition?.action?.required?.type as FieldRequirementTypeMap)) {
                validate.grispiRequiredRules = defineGrispiFieldRequirementForStatusValidation(
                    fieldDefinition,
                    lastApplicableCondition.action.required.ids
                );
            }
        }
    }
    if (fieldDefinition.key === TicketSystemFieldsKey.untrusted_end_user) {
        validate.grispiRequiredRules = (v: any, _values) => {
            const requiredErrorText = getI18n().t('form.required', { ns: 'forms' });
            if (nullishValue(v)) return `${requiredErrorText},${requiredErrorText},${requiredErrorText}`;

            const email = parseEmailFromSerializedValue(v);
            const phoneNumber = parsePhoneNumberFromSerializedValue(v);
            const fullName = parseFullNameFromSerializedUntrustedEndUserValue(v);

            const isValidFullName = fullNameValidation(fullName);

            const isValidEmail = email ? isEmail(email) : false;
            const isValidPhone = phoneNumber ? isValidPhoneNumber(phoneNumber) : false;
            return (
                (!isValidFullName && isValidEmail && isValidPhone) ||
                `${isValidFullName ? isValidFullName : ''},${
                    isValidEmail
                        ? ''
                        : getI18n().t(nullishValue(email) ? 'form.required' : 'form.email.invalid', { ns: 'forms' })
                },${
                    isValidPhone
                        ? ''
                        : getI18n().t(nullishValue(phoneNumber) ? 'form.required' : 'form.phone.invalid', {
                              ns: 'forms'
                          })
                }`
            );
        };
    }
    if (
        [customFieldTypeMap.USER, customFieldTypeMap.MULTI_USER].includes(fieldDefinition.type as 'USER' | 'MULTI_USER')
    ) {
        validate.isValid = isValidUser(fieldDefinition.key);
    }
    if (fieldDefinition.key === TicketSystemFieldsKey.subject) {
        validate.minTwoChars = (v: any, _values) =>
            v.length > 2 || (getI18n().t('form.subject.minChar', { ns: 'forms', min: 2 }) as string);
    }

    return validate;
};

export { getRequiredErrorText, getRequiredStatusErrorText };
